import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";

interface EntryEditDialogProps {
	open: boolean;
	onClose: () => void;
	title: string;
	initialText: string;
	onSave: (newText: string) => Promise<void>;
	onUpdate?: () => void;
}

export const EntryEditDialog = ({
	open,
	onClose,
	title,
	initialText,
	onSave,
	onUpdate,
}: EntryEditDialogProps) => {
	const [text, setText] = useState("");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setText(initialText);
			setError(null);
		}
	}, [open, initialText]);

	const handleSave = async () => {
		if (text === initialText) {
			onClose();
			return;
		}
		setSaving(true);
		setError(null);
		try {
			await onSave(text);
			onUpdate?.();
			onClose();
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to save");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			data-component="entry-edit-dialog"
		>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					<TextField
						multiline
						minRows={4}
						maxRows={20}
						fullWidth
						value={text}
						onChange={(e) => setText(e.target.value)}
						autoFocus
						data-component="entry-edit-text"
					/>
					{error && (
						<Typography color="error" variant="body2">
							{error}
						</Typography>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={saving || text === initialText}
					data-component="save-entry-button"
				>
					{saving ? "Saving..." : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
